import styled, { x } from '@xstyled/styled-components';
import React, { Children, Component, useEffect, useRef, useState } from 'react';
import { t } from 'ttag';
import { Item, registry, RenderProps } from '../Item';
import { AUDIO, HORIZONTAL_AUDIO_PLAYER, IMAGE, IPAD, VIDEO } from '../itemIds';
import LlamaIcon from '../../../../../assets/icons/smLlamaIcon.png';
import IpadImage from './Ipad.png';
import ArrowAndDots from './arrow_and_dots.png';
import { TrainingState, useTrainingStore } from '~/training/state';
import { Logo } from '~/ui/logo/Logo';
import { select } from '~/lib/select';

const Flex = styled.divBox`
  display: flex;
  box-sizing: border-box;
  pointer-events: none;
  > * {
    pointer-events: all;
  }
  height: 92vh;
  min-height: 600px;
  justify-content: center;
  aspect-ratio: 3 / 4;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: sm) {
    height: unset;
    min-height: unset;
  }
`;

const IpadBackground = styled.div`
  display: block;
  height: 100%;
  background-size: contain;
  background-repeat: no-repeat;
  background-image: url(${IpadImage});
  background-position: center top;
  padding-left: 40px;
  padding-right: 40px;
  padding-top: 9%;
  .fromToSection {
    margin-left: 20px;
    color: #000;
    span {
      color: ${p => p.theme.colors.accentDark};
      font-size: 14px;
      font-style: normal;
      font-weight: 600;
      line-height: normal;
      font-family: sans-serif;
      b {
        font-family: unset;
        color: #000;
        font-weight: 500;
        margin-left: 14px;
      }
    }
    .psec {
      padding-left: 50px;
    }
    p {
      font-family: var(--body-font);
      strong {
        color: #6f6f6f;
        font-variant-numeric: lining-nums proportional-nums;
        font-family: Raleway;
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: 150%;
      }
    }
  }
  @media (max-width: sm) {
    height: auto;
    background-image: unset;
    background-color: #f5f5f5;
    border: 14px solid;
    border-radius: 20px;
    padding: 3px;
    .ipadBody {
      color: #000;
    }
  }
`;

interface Props {
  from: string;
  subject: string;
  title?: string;
  body?: string;
  logo?: string;
}

const LogoIcon = styled.divBox`
  width: 60px;
  height: 60px;
  object-fit: contain;
  background-color: ${p => p.theme.colors.accentDark};
  border-radius: 50%;
  float: left;
  margin-right: 12px;
  background-image: url(${LlamaIcon});
  background-repeat: no-repeat;
  background-size: 45%;
  background-position: center;
  img {
    border-radius: 50%;
  }
`;

const Body = styled.divBox<{ h: string }>`
  color: #000;
  font-variant-numeric: lining-nums proportional-nums;
  font-family: Raleway;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  padding-left: 20px;
  padding-right: 15px;
  overflow-y: auto;
  height: ${p => p.h};

  & > p:first-child {
    background-image: url(${ArrowAndDots});
    background-repeat: no-repeat;
    background-size: 20%;
    background-position-x: right;
    background-position-y: -2px;
    padding-right: 60px;
    min-height: 30px;
  }
`;

const Inbox = styled.divBox`
  margin-top: 25px;
  margin-left: 16px;
  margin-bottom: 10px;
  color: #000;
  font-family: sans-serif;
  font-size: 18px;
  font-style: normal;
  font-weight: 800;
  line-height: normal;
  span {
    border-radius: 5px;
    background: #dcdbdb;
    color: #1f2024;
    font-size: 12px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    padding: 3px 8px;
    margin-left: 10px;
  }
`;

const Attachments = styled.divBox`
  margin-top: 20px;
  > b {
    font-size: 12px;
  }
  > div {
    float: left;
    width: 100%;
    height: 71px;
    margin-right: 18px;
    margin-top: 11px;
  }
  & .fileName {
    font-family: Raleway;
    font-style: normal;
    font-weight: normal;
    font-size: 12px;
    line-height: 150%;
  }
`;

const selector = (s: TrainingState) => select(s, 'companyName', 'logoUrl');

export class Ipad extends Item<typeof IPAD, Props> {
  protected render({ children, title, body, logo }: RenderProps<Props>) {
    const { logoUrl } = useTrainingStore(selector);

    const childArray = Children.toArray(children);
    const [height, setHeight] = useState('100%');

    const refBg = useRef<HTMLDivElement | null>(null);
    const refTitle = useRef<HTMLDivElement | null>(null);
    const refBody = useRef<HTMLDivElement | null>(null);

    let pTagsArray = body?.split(/<p>(.*?)<\/p>/);
    pTagsArray = pTagsArray?.filter(item => item.trim() !== '');

    const getFileName = (
      c: React.ReactElement<{ type?: string }>,
      i: number
    ) => {
      let fileExtension = '.jpeg';

      if (c?.props?.type == VIDEO) {
        fileExtension = '.mp4';
      }

      if (c?.props?.type == AUDIO) {
        fileExtension = '.mp3';
      }

      if (c?.props?.type == HORIZONTAL_AUDIO_PLAYER) {
        return null;
      }

      return (
        <span className="fileName">{`${c?.props?.type}${i}${fileExtension}`}</span>
      );
    };

    const handleResize = () => {
      setHeight(`100%`);

      setTimeout(() => {
        if (
          refBody &&
          refTitle &&
          refBg &&
          refBody.current &&
          refTitle.current &&
          refBg.current
        ) {
          const bodyAndTitle =
            refBody?.current?.clientHeight + refTitle?.current?.clientHeight;

          if (bodyAndTitle > refBg?.current?.clientHeight) {
            const heightForBody =
              refBg?.current?.clientHeight -
              refTitle?.current?.clientHeight -
              110;

            setHeight(`${heightForBody}px`);
          }
        }
      }, 0);
    };

    useEffect(() => {
      setTimeout(() => handleResize(), 0);

      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }, []);

    const attachmentsExist = childArray.find(c => {
      const child = c as React.ReactElement<{ type?: string }>;
      if (
        child.props.type == IMAGE ||
        child.props.type == AUDIO ||
        child.props.type == VIDEO ||
        child.props.type == HORIZONTAL_AUDIO_PLAYER
      ) {
        return true;
      }
    });

    return (
      <Flex className="nFlexDiv">
        <IpadBackground className="iPadBg" ref={refBg}>
          <div ref={refTitle}>
            <Inbox className="ipadTitle">
              {title}
              <span>{t`Inbox`}</span>
            </Inbox>
            <div className="fromToSection">
              <LogoIcon
                style={
                  logo || logoUrl
                    ? { backgroundImage: 'unset', backgroundColor: 'unset' }
                    : {}
                }
              >
                {(logo || logoUrl) && (
                  <Logo
                    src={logo || logoUrl}
                    height={65}
                    width={65}
                    style={{ objectFit: 'contain' }}
                  />
                )}
              </LogoIcon>
              <x.p
                className="psec"
                dangerouslySetInnerHTML={{
                  __html: pTagsArray && pTagsArray[0] ? pTagsArray[0] : ''
                }}
              />
            </div>
          </div>
          <Body className="ipadBody" ref={refBody} h={height}>
            {pTagsArray?.map((i, index) =>
              index === 0 ? (
                false
              ) : (
                <x.p
                  className={index == 1 ? 'firstP' : ''}
                  key={`P_${index}`}
                  dangerouslySetInnerHTML={{ __html: i }}
                />
              )
            )}
            {childArray?.map((c: Component<RenderProps<void>>, i) => {
              if (
                c.props.type == IMAGE ||
                c.props.type == VIDEO ||
                c.props.type == AUDIO ||
                c.props.type == HORIZONTAL_AUDIO_PLAYER
              ) {
                return false;
              }
              return <x.div key={i}>{c}</x.div>;
            })}
            {attachmentsExist && (
              <Attachments>
                <x.b style={{ display: 'block', clear: 'both' }}>
                  {t`Attachments`}:
                </x.b>
                {childArray.map((c, i) => {
                  const child = c as React.ReactElement<{ type?: string }>;
                  if (
                    child.props.type != IMAGE &&
                    child.props.type != AUDIO &&
                    child.props.type != VIDEO &&
                    child.props.type != HORIZONTAL_AUDIO_PLAYER
                  ) {
                    return false;
                  }
                  return (
                    <x.div key={i}>
                      {c}
                      {getFileName(child, i)}
                    </x.div>
                  );
                })}
              </Attachments>
            )}
          </Body>
        </IpadBackground>
      </Flex>
    );
  }
}

registry[IPAD] = Ipad;
